/**
 * Martha Editor CSS theme
 * Extracted from BlockNoteEditor.tsx — light theme with Martha design system.
 * Includes content-visibility optimization for large documents (Phase 2).
 */

export const editorThemeCSS = `
  .martha-editor-container {
    --bn-colors-editor-background: #ffffff;
    --bn-colors-editor-text: #1c1917;
    --bn-colors-menu-background: #ffffff;
    --bn-colors-menu-text: #1c1917;
    --bn-colors-tooltip-background: #1c1917;
    --bn-colors-tooltip-text: #ffffff;
    --bn-colors-hovered-background: #f5f5f4;
    --bn-colors-selected-background: #fef2f2;
    --bn-colors-highlights-gray-background: #f5f5f4;
    --bn-colors-highlights-gray-text: #1c1917;
    --bn-font-family: 'Source Serif 4', Georgia, 'Times New Roman', serif;
  }

  .martha-editor-container .bn-editor {
    padding: 1rem 2rem;
    min-height: 100%;
    background-color: #ffffff !important;
    color: #1c1917 !important;
    font-family: 'Source Serif 4', Georgia, 'Times New Roman', serif !important;
  }

  .martha-editor-container .bn-block-content {
    font-size: 1rem;
    line-height: 1.75;
    color: #1c1917 !important;
  }

  /* Force light theme text colors to prevent blackouts */
  .martha-editor-container .bn-inline-content,
  .martha-editor-container .bn-inline-content * {
    color: inherit !important;
  }

  .martha-editor-container [data-content-type]:not([data-content-type="codeBlock"]) {
    color: #1c1917 !important;
  }

  .martha-editor-container .ProseMirror {
    color: #1c1917 !important;
  }

  .martha-editor-container p,
  .martha-editor-container span,
  .martha-editor-container div {
    color: inherit;
  }

  .martha-editor-container h1 {
    font-size: 2rem;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #1c1917;
  }

  .martha-editor-container h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #292524;
  }

  .martha-editor-container h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 0;
    margin-bottom: 0.5rem;
    color: #44403c;
  }

  /* Heading spacing on block-outer so side menu aligns with heading text */
  .martha-editor-container .bn-block-outer:has([data-content-type="heading"][data-level="1"]) {
    margin-top: 1.5rem;
  }
  .martha-editor-container .bn-block-outer:has([data-content-type="heading"][data-level="2"]) {
    margin-top: 1.25rem;
  }
  .martha-editor-container .bn-block-outer:has([data-content-type="heading"][data-level="3"]) {
    margin-top: 1rem;
  }

  .martha-editor-container p {
    margin-bottom: 0.5rem;
  }

  .martha-editor-container ul,
  .martha-editor-container ol {
    padding-left: 1.5rem;
  }

  /* Inline code — gray pill style (exclude code inside pre blocks) */
  .martha-editor-container code:not(pre code) {
    background-color: #f5f5f4;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-family: 'Fira Code', 'Monaco', monospace;
    font-size: 0.875em;
  }

  /* Code inside pre blocks — transparent bg, inherit colors from pre */
  .martha-editor-container pre code {
    background-color: transparent !important;
    padding: 0 !important;
    border-radius: 0 !important;
    font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
    font-size: 0.875rem;
  }

  .martha-editor-container pre {
    background-color: #1c1917;
    color: #fafaf9;
    padding: 1rem;
    border-radius: 0.5rem;
    overflow-x: auto;
  }

  /* Code block — refined dark panel with warm stone tones */
  .martha-editor-container .bn-block-content[data-content-type="codeBlock"] {
    color: #fafaf9 !important;
    margin: 0.75rem 0;
  }

  .martha-editor-container .bn-block-content[data-content-type="codeBlock"] pre {
    color: #e7e5e4 !important;
    background: linear-gradient(180deg, #1c1917 0%, #171412 100%);
    font-family: 'Fira Code', 'JetBrains Mono', 'SF Mono', 'Cascadia Code', 'Consolas', monospace;
    font-size: 0.8125rem;
    line-height: 1.7;
    letter-spacing: 0.01em;
    padding: 1.25rem 1.5rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.04);
    overflow-x: auto;
    tab-size: 2;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .martha-editor-container .bn-block-content[data-content-type="codeBlock"] .bn-inline-content,
  .martha-editor-container .bn-block-content[data-content-type="codeBlock"] .bn-inline-content * {
    color: #e7e5e4 !important;
  }

  .martha-editor-container blockquote {
    border-left: 3px solid #f97316;
    padding-left: 1rem;
    margin-left: 0;
    color: #57534e;
    font-style: italic;
  }

  .martha-editor-container a {
    color: #f97316;
    text-decoration: underline;
  }

  .martha-editor-container a:hover {
    color: #ea580c;
  }

  /* Placeholder styling */
  .martha-editor-container .bn-inline-content[data-placeholder]::before {
    color: #a8a29e;
  }

  /* Focus styles */
  .martha-editor-container .bn-editor:focus-within {
    outline: none;
  }

  /* Selection highlight */
  .martha-editor-container ::selection {
    background-color: #fed7aa;
  }

  /* Vertically center the 6-dot drag handle with heading text */
  .martha-editor-container .bn-block-outer:has([data-content-type="heading"]) > .bn-block-content-wrapper > .bn-side-menu,
  .martha-editor-container .bn-block-outer:has([data-content-type="heading"]) .bn-side-menu {
    top: 50% !important;
    transform: translateY(-50%);
  }

  /* Keep editor menus/toolbars in the UI font (Geist) */
  .martha-editor-container .bn-side-menu,
  .martha-editor-container .bn-formatting-toolbar,
  .martha-editor-container .bn-slash-menu,
  .martha-editor-container .bn-suggestion-menu,
  .martha-editor-container .bn-toolbar,
  .martha-editor-container .bn-color-picker {
    font-family: 'Geist', system-ui, -apple-system, sans-serif !important;
  }

  /* Phase 2: content-visibility removed — causes gray placeholder bars in large
     documents because the browser collapses blocks to contain-intrinsic-size
     during chunked insertion, and some blocks never recover to full rendering.
     The fast parser + chunked loading provide sufficient perf without this. */
`;
